import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Bookmark, BookmarkCheck, Globe, MessageSquare, MessageSquareOff, Pencil, Trash2, ArrowLeft } from 'lucide-react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { caseService } from '../api/services/case.service'
import { assistantService } from '../api/services/assistant.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { CaseCommentsPanel } from '../components/cases/CaseCommentsPanel'
import { CaseFilesGallery } from '../components/cases/CaseFilesGallery'
import { CaseOutcomePanel } from '../components/cases/CaseOutcomePanel'
import {
  CaseStatusBadge,
  CaseStateBadge,
} from '../components/cases/CaseStatusBadge'
import {
  buildCaseFilesFormData,
  CaseMediaUploader,
  type PendingCaseMedia,
} from '../components/cases/CaseMediaUploader'
import { REVIEWER_TYPE_LABELS } from '../components/cases/CaseReviewerPicker'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { SoftDeleteConfirmDialog } from '../components/ui/soft-delete-confirm-dialog'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { useAuth } from '../context/AuthContext'
import { useNotifications } from '../context/NotificationContext'
import { useCaseCommentsRealtime } from '../hooks/useCaseCommentsRealtime'
import {
  canDeleteCase,
  canMutateCase,
  canSetGlobalOnCase,
  canShowCaseReviewersTab,
  canUpdateOutcomeOnCase,
  isAssignedReviewerView,
} from '../lib/case-capabilities'
import { canShowCaseOutcomeTab } from '../lib/case-phase'
import {
  type Case,
  type CaseActivity,
  type CaseComment,
  type CaseFile,
  type CaseReviewer,
} from '../types/case'
import type { AssistantDoctorLink } from '../types/assistant'
import { USER_ROLES } from '../types/auth'

type DetailTab = 'overview' | 'outcome' | 'reviewers' | 'comments' | 'files' | 'activity'

const DETAIL_TABS: Array<{ key: DetailTab; label: string; permission?: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'files', label: 'Files', permission: 'case-files-list' },
  { key: 'outcome', label: 'Outcome', permission: 'cases-outcome-view' },
  { key: 'reviewers', label: 'Feedback', permission: 'cases-reviewers-list' },
  { key: 'comments', label: 'National Comments', permission: 'case-comments-list' },
  { key: 'activity', label: 'Activity', permission: 'case-activity-list' },
]

function displayValue(value: string | null | undefined, empty = 'Not set') {
  const trimmed = value?.trim()
  return trimmed ? trimmed : empty
}

function personInitials(name: string | null | undefined) {
  if (!name?.trim()) {
    return '?'
  }

  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((part) => part.charAt(0).toUpperCase()).join('')
}

function SummaryRow({
  label,
  value,
  empty = false,
}: {
  label: string
  value: string
  empty?: boolean
}) {
  return (
    <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 border-b border-parch-100 py-2.5 last:border-b-0">
      <dt className="pt-0.5 text-xs font-medium tracking-wide text-ink-400 uppercase">
        {label}
      </dt>
      <dd
        className={`min-w-0 text-sm font-medium break-words ${
          empty ? 'font-normal text-ink-400' : 'text-ink-800'
        }`}
      >
        {value}
      </dd>
    </div>
  )
}

function OverviewCard({
  title,
  children,
  className,
}: {
  title: string
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={`rounded-2xl border border-parch-100 bg-white p-5 shadow-card ${className ?? ''}`}
    >
      <h3 className="font-display text-sm font-semibold text-ink-800">{title}</h3>
      <div className="mt-4">{children}</div>
    </section>
  )
}

function PersonRow({
  label,
  name,
  emphasize,
}: {
  label: string
  name: string | null | undefined
  emphasize?: boolean
}) {
  const displayName = name?.trim() || '—'

  return (
    <div className="flex items-center gap-3 py-2">
      <span
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-semibold ${
          emphasize
            ? 'bg-green-600 text-white'
            : 'bg-green-100 text-green-700'
        }`}
      >
        {personInitials(name)}
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium tracking-wide text-ink-400 uppercase">
          {label}
        </p>
        <p className="truncate text-sm font-medium text-ink-800">{displayName}</p>
      </div>
    </div>
  )
}

function FindingsValue({ items }: { items: string[] }) {
  if (items.length === 0) {
    return <span className="font-normal text-ink-400">None recorded</span>
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span
          key={item}
          className="inline-flex rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
        >
          {item}
        </span>
      ))}
    </div>
  )
}

function OverviewSection({ caseItem }: { caseItem: Case }) {
  const comorbidities = caseItem.comorbidities?.map((item) => item.name) ?? []
  const complications = caseItem.complications?.map((item) => item.name) ?? []
  const ageValue =
    caseItem.age != null ? `${caseItem.age} yrs` : 'Not set'
  const operativeTime =
    caseItem.operative_time != null
      ? `${caseItem.operative_time} min`
      : 'Not set'

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(16rem,0.9fr)]">
      <div className="space-y-4">
        <OverviewCard title="Clinical summary">
          <dl>
            <SummaryRow
              label="Age"
              value={ageValue}
              empty={caseItem.age == null}
            />
            <SummaryRow
              label="Gender"
              value={displayValue(caseItem.gender)}
              empty={!caseItem.gender?.trim()}
            />
            <SummaryRow
              label="Procedure"
              value={displayValue(caseItem.procedure?.name)}
              empty={!caseItem.procedure?.name?.trim()}
            />
            <SummaryRow
              label="Department"
              value={displayValue(caseItem.department?.name)}
              empty={!caseItem.department?.name?.trim()}
            />
            <SummaryRow
              label="Procedure date"
              value={displayValue(caseItem.procedure_date)}
              empty={!caseItem.procedure_date?.trim()}
            />
            <SummaryRow
              label="Operative time"
              value={operativeTime}
              empty={caseItem.operative_time == null}
            />
          </dl>
        </OverviewCard>

        <OverviewCard title="Case description">
          <p
            className={`whitespace-pre-line text-sm leading-relaxed ${
              caseItem.description?.trim() ? 'text-ink-800' : 'text-ink-400'
            }`}
          >
            {caseItem.description?.trim() || 'No description added.'}
          </p>
        </OverviewCard>

        <OverviewCard title="Notes & history">
          <div>
            <p className="text-xs font-medium tracking-wide text-ink-400 uppercase">
              Past medical history
            </p>
            <p
              className={`mt-1 whitespace-pre-line text-sm leading-relaxed ${
                caseItem.past_medical_history?.trim()
                  ? 'text-ink-800'
                  : 'text-ink-400'
              }`}
            >
              {caseItem.past_medical_history?.trim() || 'Nothing recorded yet.'}
            </p>
          </div>
          <div className="mt-4 border-t border-parch-100 pt-4">
            <p className="text-xs font-medium tracking-wide text-ink-400 uppercase">
              Notes
            </p>
            <p
              className={`mt-1 whitespace-pre-line text-sm leading-relaxed ${
                caseItem.note?.trim() ? 'text-ink-800' : 'text-ink-400'
              }`}
            >
              {caseItem.note?.trim() || 'No clinical notes added.'}
            </p>
          </div>
          {caseItem.outcome?.trim() ? (
            <div className="mt-4 border-t border-parch-100 pt-4">
              <p className="text-xs font-medium tracking-wide text-ink-400 uppercase">
                Submission outcome
              </p>
              <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-ink-800">
                {caseItem.outcome}
              </p>
            </div>
          ) : null}
        </OverviewCard>
      </div>

      <div className="space-y-4">
        <OverviewCard title="People">
          <PersonRow
            label="Consultant"
            name={caseItem.belongs_to_doctor?.name}
            emphasize
          />
          <PersonRow label="Uploaded by" name={caseItem.uploaded_by?.name} />
        </OverviewCard>

        <OverviewCard title="Clinical findings">
          <dl>
            <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 border-b border-parch-100 py-2.5">
              <dt className="pt-0.5 text-xs font-medium tracking-wide text-ink-400 uppercase">
                Comorbidities
              </dt>
              <dd className="min-w-0 text-sm font-medium text-ink-800">
                <FindingsValue items={comorbidities} />
              </dd>
            </div>
            <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 py-2.5">
              <dt className="pt-0.5 text-xs font-medium tracking-wide text-ink-400 uppercase">
                Complications
              </dt>
              <dd className="min-w-0 text-sm font-medium text-ink-800">
                <FindingsValue items={complications} />
              </dd>
            </div>
          </dl>
        </OverviewCard>

        {caseItem.is_under_review ? (
          <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-3.5">
            <p className="text-sm font-semibold text-ink-800">Active review open</p>
            <p className="mt-1 text-sm leading-relaxed text-ink-600">
              Reviewers can discuss this case. Close review when collaboration is complete.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function CaseDetailPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const { can, session } = useAuth()
  const { commentUnreadCount, markCaseCommentsRead, subscribe } = useNotifications()
  const isNationalHubRoute = location.pathname.startsWith('/national-hub')
  const casesBackTo = isNationalHubRoute ? '/national-hub' : '/cases'
  const casesBackLabel = isNationalHubRoute ? 'Back to National Hub' : 'Back to Cases'

  const [activeTab, setActiveTab] = useState<DetailTab>(
    isNationalHubRoute ? 'comments' : 'overview',
  )
  const [caseItem, setCaseItem] = useState<Case | null>(null)
  const [linkedDoctors, setLinkedDoctors] = useState<AssistantDoctorLink[]>([])
  const [reviewers, setReviewers] = useState<CaseReviewer[]>([])
  const [reviewComments, setReviewComments] = useState<CaseComment[]>([])
  const [nationalComments, setNationalComments] = useState<CaseComment[]>([])
  const [selectedReviewerUserId, setSelectedReviewerUserId] = useState<string | null>(null)
  const [files, setFiles] = useState<CaseFile[]>([])
  const [activity, setActivity] = useState<CaseActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const [commentBody, setCommentBody] = useState('')
  const [commentChannel, setCommentChannel] = useState<'review' | 'national'>(
    isNationalHubRoute ? 'national' : 'review',
  )
  const [postAnonymously, setPostAnonymously] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingCommentBody, setEditingCommentBody] = useState('')

  const [pendingMedia, setPendingMedia] = useState<PendingCaseMedia[]>([])

  const canListComments = can('case-comments-list')
  const canListFiles = can('case-files-list')
  const canListActivity = can('case-activity-list')
  const canViewOutcome = can('cases-outcome-view')
  const canListReviewers = canShowCaseReviewersTab(
    can,
    session?.role,
    caseItem,
    linkedDoctors,
    session?.user.id,
  )
  const canMutateCurrentCase =
    caseItem !== null &&
    canMutateCase(session?.role, caseItem, linkedDoctors, session?.user.id)
  const canDeleteCurrentCase =
    caseItem !== null &&
    canDeleteCase(session?.role, caseItem, linkedDoctors, session?.user.id)
  const canSetGlobal =
    caseItem !== null &&
    canSetGlobalOnCase(session?.role, caseItem, linkedDoctors, session?.user.id)
  const canUpdateOutcome =
    caseItem !== null &&
    can('cases-outcome-update') &&
    canUpdateOutcomeOnCase(session?.role, caseItem, linkedDoctors, session?.user.id)
  // Reviewer-only restrictions apply to Cases for Review — not National Hub browsing.
  const isReviewerOnly =
    caseItem !== null &&
    !isNationalHubRoute &&
    isAssignedReviewerView(caseItem, session?.user.id)

  // National Hub visitor: case is not this doctor's (or their assistant's upload).
  const isNationalHubVisitor =
    isNationalHubRoute &&
    caseItem !== null &&
    !canMutateCurrentCase &&
    session?.role !== USER_ROLES.SUPER_ADMIN

  const outcomeTabEnabled =
    caseItem !== null && canShowCaseOutcomeTab(caseItem)

  const visibleTabs = useMemo(
    () =>
      DETAIL_TABS.filter((tab) => {
        // National Hub — other doctors' cases: Overview + Files + National Comments only.
        if (isNationalHubVisitor) {
          return tab.key === 'overview' || tab.key === 'files' || tab.key === 'comments'
        }

        // National Hub — own case: hide Feedback (review thread); keep National Comments.
        if (isNationalHubRoute && tab.key === 'reviewers') {
          return false
        }

        // Assigned reviewer (Cases for Review): Overview + Files + Feedback.
        if (isReviewerOnly) {
          return tab.key === 'overview' || tab.key === 'files' || tab.key === 'reviewers'
        }

        if (!tab.permission) {
          return true
        }

        if (tab.key === 'outcome') {
          return canViewOutcome
        }

        if (tab.key === 'reviewers') {
          return canListReviewers
        }

        if (tab.key === 'comments') {
          return canListComments && Boolean(caseItem?.is_global)
        }

        if (tab.key === 'files') {
          return canListFiles
        }

        if (tab.key === 'activity') {
          return canListActivity
        }

        return can(tab.permission)
      }).map((tab) => ({
        ...tab,
        disabled: tab.key === 'outcome' && !outcomeTabEnabled,
        disabledReason:
          tab.key === 'outcome' && !outcomeTabEnabled
            ? 'Available after the case is published and the procedure date has passed.'
            : undefined,
      })),
    [
      can,
      canListActivity,
      canListComments,
      canListFiles,
      canListReviewers,
      canViewOutcome,
      caseItem?.is_global,
      isNationalHubRoute,
      isNationalHubVisitor,
      isReviewerOnly,
      outcomeTabEnabled,
    ],
  )

  const loadCase = useCallback(async () => {
    if (!id) {
      return
    }

    const response = await caseService.show(id)
    setCaseItem(response.data)
    setIsBookmarked(Boolean(response.data.is_bookmarked))
  }, [id])

  const loadReviewComments = useCallback(async () => {
    // Assigned reviewers can load the review thread even without a cached list permission.
    if (!id || (!canListComments && !isReviewerOnly && !caseItem?.is_assigned_reviewer)) {
      setReviewComments([])
      return
    }

    try {
      const response = await caseService.listComments(id, { channel: 'review' })
      setReviewComments(Array.isArray(response.data) ? response.data : [])
    } catch {
      setReviewComments([])
    }
  }, [canListComments, caseItem?.is_assigned_reviewer, id, isReviewerOnly])

  const loadReviewers = useCallback(async () => {
    // Reviewer-only viewers only need Feedback — skip the reviewers list API.
    if (!id || !canListReviewers || isReviewerOnly) {
      setReviewers([])
      return
    }

    try {
      const response = await caseService.listReviewers(id)
      setReviewers(Array.isArray(response.data) ? response.data : [])
    } catch {
      setReviewers([])
    }
  }, [canListReviewers, id, isReviewerOnly])

  const loadNationalComments = useCallback(async () => {
    if (!id || !canListComments || isReviewerOnly) {
      setNationalComments([])
      return
    }

    try {
      const response = await caseService.listComments(id, { channel: 'national' })
      setNationalComments(Array.isArray(response.data) ? response.data : [])
    } catch {
      setNationalComments([])
    }
  }, [canListComments, id, isReviewerOnly])

  const loadFiles = useCallback(async () => {
    if (!id || !canListFiles) {
      setFiles([])
      return
    }

    try {
      const response = await caseService.listFiles(id)
      setFiles(Array.isArray(response.data) ? response.data : [])
    } catch {
      setFiles([])
    }
  }, [canListFiles, id])

  const loadActivity = useCallback(async () => {
    if (!id || !canListActivity) {
      setActivity([])
      return
    }

    try {
      const response = await caseService.listActivity(id)
      setActivity(Array.isArray(response.data) ? response.data : [])
    } catch {
      setActivity([])
    }
  }, [canListActivity, id])

  const reloadAll = useCallback(async () => {
    if (!id) {
      return
    }

    setLoading(true)

    try {
      await loadCase()
    } catch {
      setCaseItem(null)
      setLoading(false)
      return
    }

    setLoading(false)
  }, [id, loadCase])

  const loadActiveTabData = useCallback(async () => {
    if (!id || !caseItem) {
      return
    }

    if (activeTab === 'reviewers') {
      await Promise.all([loadReviewers(), loadReviewComments()])
      return
    }

    if (activeTab === 'comments') {
      await loadNationalComments()
      return
    }

    if (activeTab === 'files' || activeTab === 'outcome') {
      await loadFiles()
      return
    }

    if (activeTab === 'activity') {
      await loadActivity()
    }
  }, [
    activeTab,
    caseItem,
    id,
    loadActivity,
    loadNationalComments,
    loadReviewComments,
    loadFiles,
    loadReviewers,
  ])

  useEffect(() => {
    void reloadAll()
  }, [reloadAll])

  useEffect(() => {
    void loadActiveTabData()
  }, [loadActiveTabData])

  useEffect(() => {
    if (session?.role !== USER_ROLES.ASSISTANT) {
      setLinkedDoctors([])
      return
    }

    // Prefer session flags (dual-gate with permissions); refresh from API if needed.
    if (session.assistant_links && session.assistant_links.length > 0) {
      setLinkedDoctors(session.assistant_links as AssistantDoctorLink[])
    }

    void assistantService
      .linkedDoctors()
      .then((response) => {
        setLinkedDoctors(Array.isArray(response.data) ? response.data : [])
      })
      .catch(() => {
        if (!session.assistant_links?.length) {
          setLinkedDoctors([])
        }
      })
  }, [session?.assistant_links, session?.role])

  useEffect(() => {
    const active = visibleTabs.find((tab) => tab.key === activeTab)
    if (!active || active.disabled) {
      setActiveTab(visibleTabs.find((tab) => !tab.disabled)?.key ?? 'overview')
    }
  }, [activeTab, visibleTabs])

  const handleRealtimeCommentCreated = useCallback((comment: CaseComment) => {
    const channel = comment.channel ?? 'review'
    const normalized: CaseComment = {
      ...comment,
      channel,
      tag: comment.tag ?? (channel === 'national' ? 'national' : 'pre_op'),
    }
    const setter = channel === 'national' ? setNationalComments : setReviewComments
    setter((current) => {
      const existing = current.find((item) => item.id === normalized.id)
      if (existing) {
        // HTTP response may arrive after broadcast (broadcast has is_own: false).
        return current.map((item) =>
          item.id === normalized.id
            ? {
                ...item,
                ...normalized,
                is_own: Boolean(item.is_own || normalized.is_own),
                user_id: normalized.user_id ?? item.user_id,
                user: normalized.user ?? item.user,
              }
            : item,
        )
      }

      return [normalized, ...current]
    })
  }, [])

  const handleRealtimeCommentUpdated = useCallback((comment: CaseComment) => {
    const channel = comment.channel ?? 'review'
    const setter = channel === 'national' ? setNationalComments : setReviewComments
    setter((current) =>
      current.map((existing) => {
        if (existing.id !== comment.id) {
          return existing
        }

        // Broadcast resolves CommentResource without a viewer, so is_own is always false.
        // Keep local ownership so Edit/Delete stay visible after save.
        return {
          ...existing,
          ...comment,
          is_own: Boolean(existing.is_own || comment.is_own),
          user_id: comment.user_id ?? existing.user_id,
          user: comment.user ?? existing.user,
        }
      }),
    )
  }, [])

  const handleRealtimeCommentDeleted = useCallback((payload: { id: string }) => {
    setReviewComments((current) => current.filter((existing) => existing.id !== payload.id))
    setNationalComments((current) => current.filter((existing) => existing.id !== payload.id))
  }, [])

  useCaseCommentsRealtime(id, {
    onCreated: handleRealtimeCommentCreated,
    onUpdated: handleRealtimeCommentUpdated,
    onDeleted: handleRealtimeCommentDeleted,
  })

  // Safety net: if case-channel broadcast is missed but the bell notification arrives,
  // reload the open feedback thread so peers still see new comments.
  useEffect(() => {
    if (!id) {
      return
    }

    return subscribe((notification) => {
      if (notification.type !== 'comment_created' || notification.case_id !== id) {
        return
      }

      const channel =
        typeof notification.data?.channel === 'string'
          ? notification.data.channel
          : 'review'

      if (channel === 'national') {
        void loadNationalComments()
        return
      }

      void loadReviewComments()
    })
  }, [id, loadNationalComments, loadReviewComments, subscribe])

  const unreadComments = id ? commentUnreadCount(id) : 0

  useEffect(() => {
    if (
      (activeTab !== 'reviewers' && activeTab !== 'comments') ||
      !id ||
      unreadComments === 0
    ) {
      return
    }

    void markCaseCommentsRead(id).catch(() => {
      // handled globally
    })
  }, [activeTab, id, markCaseCommentsRead, unreadComments])

  const runAction = async (action: () => Promise<unknown>) => {
    setActionLoading(true)

    try {
      await action()
      await reloadAll()
    } catch {
      // handled globally
    } finally {
      setActionLoading(false)
    }
  }

  const handleGlobalToggle = (nextGlobal: boolean) => {
    if (!id || !caseItem) {
      return
    }

    void runAction(async () => {
      await caseService.update(id, {
        is_global: nextGlobal,
        allow_national_comments: nextGlobal ? Boolean(caseItem.allow_national_comments) : false,
      })
    })
  }

  const handleNationalCommentsToggle = (nextAllowed: boolean) => {
    if (!id || !caseItem || !caseItem.is_global) {
      return
    }

    void runAction(async () => {
      await caseService.update(id, {
        allow_national_comments: nextAllowed,
      })
    })
  }

  const handleBookmarkToggle = async () => {
    if (!id) {
      return
    }

    setActionLoading(true)

    try {
      const response = await caseService.toggleBookmark(id, isBookmarked ? 0 : 1)
      setIsBookmarked(response.data.bookmarked)
    } catch {
      // handled globally
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!id) {
      return
    }

    setActionLoading(true)

    try {
      await caseService.remove(id)
      setDeleteOpen(false)
      navigate(casesBackTo)
    } catch {
      // handled globally
    } finally {
      setActionLoading(false)
    }
  }

  const handleAddComment = (channel: 'review' | 'national' = commentChannel) => {
    if (!id || !commentBody.trim()) {
      return
    }

    const body = commentBody.trim()
    const anonymous = postAnonymously

    setActionLoading(true)

    void (async () => {
      try {
        const response = await caseService.createComment(id, {
          body,
          channel,
          is_anonymous: anonymous || undefined,
        })
        if (response.data) {
          handleRealtimeCommentCreated(response.data)
        }
        setCommentBody('')
        setPostAnonymously(false)
      } catch {
        // handled globally
      } finally {
        setActionLoading(false)
      }
    })()
  }

  const handleSaveComment = (commentId: string) => {
    if (!id || !editingCommentBody.trim()) {
      return
    }

    const body = editingCommentBody.trim()

    setActionLoading(true)

    void (async () => {
      try {
        const response = await caseService.updateComment(id, commentId, { body })
        if (response.data) {
          handleRealtimeCommentUpdated(response.data)
        } else {
          const patch = (current: CaseComment[]) =>
            current.map((comment) => (comment.id === commentId ? { ...comment, body } : comment))
          setReviewComments(patch)
          setNationalComments(patch)
        }
        setEditingCommentId(null)
        setEditingCommentBody('')
      } catch {
        // handled globally
      } finally {
        setActionLoading(false)
      }
    })()
  }

  const handleDeleteComment = (commentId: string) => {
    if (!id) {
      return
    }

    setActionLoading(true)

    void (async () => {
      try {
        await caseService.deleteComment(id, commentId)
        handleRealtimeCommentDeleted({ id: commentId })
      } catch {
        // handled globally
      } finally {
        setActionLoading(false)
      }
    })()
  }

  const handleUploadFiles = (media: PendingCaseMedia[]) => {
    if (!id || media.length === 0) {
      return
    }

    void runAction(async () => {
      await caseService.uploadFiles(id, buildCaseFilesFormData(media))
      setPendingMedia([])
    })
  }

  const handleDownloadFile = async (fileId: string) => {
    if (!id) {
      return
    }

    const response = await caseService.downloadFile(id, fileId)
    if (response.data.url) {
      window.open(response.data.url, '_blank', 'noopener,noreferrer')
    }
  }

  const handleDeleteFile = (fileId: string) => {
    if (!id) {
      return
    }

    void runAction(() => caseService.deleteFile(id, fileId))
  }

  const isUnderReview = Boolean(caseItem?.is_under_review)
  const isReviewClosed = Boolean(caseItem?.is_review_closed)
  const isArchived = Boolean(caseItem?.is_archived)

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-center gap-2">
                <IconActionButton
                  icon={ArrowLeft}
                  label={casesBackLabel}
                  to={casesBackTo}
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-display text-2xl font-bold">
                  {caseItem?.reference ?? 'Case'}
                </h1>
                {caseItem?.status && <CaseStatusBadge status={caseItem.status} />}
                {caseItem?.is_under_review && <CaseStateBadge state="under_review" />}
                {caseItem?.is_review_closed && <CaseStateBadge state="review_closed" />}
                {caseItem?.is_archived && <CaseStateBadge state="archived" />}
              </div>
            </div>

            {caseItem && !caseItem.deleted_at && (
              <TableIconActions className="shrink-0">
                {!isReviewerOnly && (
                  <IconActionButton
                    icon={isBookmarked ? BookmarkCheck : Bookmark}
                    label={isBookmarked ? 'Remove Bookmark' : 'Bookmark'}
                    disabled={actionLoading}
                    onClick={() => void handleBookmarkToggle()}
                  />
                )}
                {can('cases-update') && canSetGlobal && !isArchived && (
                  <IconActionButton
                    icon={Globe}
                    label={caseItem.is_global ? 'Make Private' : 'Make Public'}
                    disabled={actionLoading}
                    onClick={() => handleGlobalToggle(!caseItem.is_global)}
                    className={caseItem.is_global ? 'text-info' : undefined}
                  />
                )}
                {can('cases-update') &&
                  canSetGlobal &&
                  !isArchived &&
                  caseItem.is_global && (
                    <IconActionButton
                      icon={
                        caseItem.allow_national_comments ? MessageSquare : MessageSquareOff
                      }
                      label={
                        caseItem.allow_national_comments
                          ? 'Stop National Hub Comments'
                          : 'Allow National Hub Comments'
                      }
                      disabled={actionLoading}
                      onClick={() =>
                        handleNationalCommentsToggle(!Boolean(caseItem.allow_national_comments))
                      }
                      className={
                        caseItem.allow_national_comments ? 'text-info' : 'text-ink-400'
                      }
                    />
                  )}
                {caseItem.is_global &&
                  !(can('cases-update') && canSetGlobal && !isArchived) &&
                  !isReviewerOnly && (
                    <IconActionButton
                      icon={Globe}
                      label="Public Case"
                      disabled
                      className="text-info"
                    />
                  )}
                {can('cases-update') && canMutateCurrentCase && !isArchived && (
                  <IconActionButton
                    icon={Pencil}
                    label="Edit"
                    to={`/cases/${caseItem.id}/edit`}
                  />
                )}
                {can('cases-delete') && canDeleteCurrentCase && (
                  <IconActionButton
                    icon={Trash2}
                    label="Delete"
                    variant="destructive"
                    disabled={actionLoading}
                    onClick={() => setDeleteOpen(true)}
                  />
                )}
              </TableIconActions>
            )}
          </div>
        </div>

        {!loading && !caseItem ? (
          <p className="text-sm text-danger">Case not found.</p>
        ) : caseItem ? (
          <>
            {isReviewerOnly && (
              <div className="mb-4 rounded-md border border-info/20 bg-info-lt px-4 py-3 text-sm text-info">
                You are viewing this case as an assigned reviewer. You can review the
                case and participate in comments, but cannot edit it or perform case
                workflow actions.
              </div>
            )}

            <div className="mb-4 flex flex-wrap gap-2 border-b border-parch-100">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  disabled={tab.disabled}
                  title={tab.disabledReason}
                  aria-disabled={tab.disabled}
                  className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                    tab.disabled
                      ? 'cursor-not-allowed border-transparent text-ink-300'
                      : activeTab === tab.key
                        ? 'border-green-600 text-green-800'
                        : 'border-transparent text-ink-600 hover:text-ink-900'
                  }`}
                  onClick={() => {
                    if (!tab.disabled) {
                      setActiveTab(tab.key)
                    }
                  }}
                >
                  {tab.label}
                  {(tab.key === 'reviewers' || tab.key === 'comments') &&
                    unreadComments > 0 && (
                    <span className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-green-600 px-1.5 py-0.5 text-xs font-semibold text-white">
                      {unreadComments > 99 ? '99+' : unreadComments}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <section
              className={
                activeTab === 'overview'
                  ? 'bg-transparent'
                  : 'rounded-lg border border-parch-100 bg-white p-6 shadow-card'
              }
            >
              {activeTab === 'overview' && <OverviewSection caseItem={caseItem} />}

              {activeTab === 'outcome' && outcomeTabEnabled && (
                <CaseOutcomePanel
                  caseItem={caseItem}
                  files={files}
                  canView={canViewOutcome}
                  canUpdate={canUpdateOutcome}
                  canUploadFiles={can('case-files-upload') && !isReviewerOnly}
                  canDownloadFiles={can('case-files-download')}
                  canDeleteFiles={can('case-files-delete') && !isReviewerOnly}
                  actionLoading={actionLoading}
                  onFilesChanged={loadFiles}
                />
              )}

              {activeTab === 'reviewers' && (
                <div className="space-y-8">
                  {!isReviewerOnly && (
                    <div>
                      <h2 className="font-display text-lg font-semibold">Assigned reviewers</h2>
                      <p className="mt-1 text-sm text-ink-600">
                        Assigned reviewers (max 2). Click a reviewer to see their review
                        feedback. Add or change reviewers from the case edit form.
                      </p>
                      <ul className="mt-3 space-y-2">
                        {reviewers.length === 0 ? (
                          <li className="text-sm text-ink-500">No reviewers assigned.</li>
                        ) : (
                          reviewers.map((reviewer) => {
                            const selected = selectedReviewerUserId === reviewer.user_id
                            return (
                              <li key={reviewer.id}>
                                <button
                                  type="button"
                                  className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                                    selected
                                      ? 'border-green-600 bg-green-50'
                                      : 'border-parch-100 bg-parch-50 hover:border-green-400'
                                  }`}
                                  onClick={() => {
                                    setSelectedReviewerUserId((current) =>
                                      current === reviewer.user_id ? null : reviewer.user_id,
                                    )
                                    setCommentChannel('review')
                                  }}
                                >
                                  <span className="font-medium text-ink-800">
                                    {reviewer.user?.name ?? reviewer.user_id}
                                  </span>
                                  {reviewer.reviewer_type && (
                                    <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
                                      {REVIEWER_TYPE_LABELS[reviewer.reviewer_type]}
                                    </span>
                                  )}
                                  {reviewer.user?.email && (
                                    <span className="ml-2 text-ink-500">
                                      ({reviewer.user.email})
                                    </span>
                                  )}
                                </button>
                              </li>
                            )
                          })
                        )}
                      </ul>
                      {selectedReviewerUserId ? (
                        <button
                          type="button"
                          className="mt-2 text-xs font-medium text-green-800 hover:underline"
                          onClick={() => setSelectedReviewerUserId(null)}
                        >
                          Show all review feedback
                        </button>
                      ) : null}
                    </div>
                  )}
                  {!isReviewerOnly &&
                    can('cases-update') &&
                    canMutateCurrentCase &&
                    !isArchived && (
                      <Button variant="secondary" size="sm" asChild>
                        <Link to={`/cases/${caseItem.id}/edit`}>Edit Reviewers</Link>
                      </Button>
                    )}

                  {canListComments ||
                  isReviewerOnly ||
                  Boolean(caseItem.is_assigned_reviewer) ? (
                    <CaseCommentsPanel
                      caseItem={caseItem}
                      comments={reviewComments}
                      channel="review"
                      filterUserId={isReviewerOnly ? null : selectedReviewerUserId}
                      isConsultant={session?.user.id === caseItem.belongs_to_doctor_id}
                      canStore={
                        can('case-comments-store') ||
                        isReviewerOnly ||
                        Boolean(caseItem.is_assigned_reviewer)
                      }
                      canUpdate={can('case-comments-update')}
                      canDelete={can('case-comments-delete')}
                      isUnderReview={isUnderReview}
                      actionLoading={actionLoading}
                      commentBody={commentBody}
                      onCommentBodyChange={(value) => {
                        setCommentChannel('review')
                        setCommentBody(value)
                      }}
                      postAnonymously={postAnonymously}
                      onPostAnonymouslyChange={setPostAnonymously}
                      onAddComment={() => handleAddComment('review')}
                      editingCommentId={editingCommentId}
                      editingCommentBody={editingCommentBody}
                      onEditingCommentBodyChange={setEditingCommentBody}
                      onStartEdit={(comment) => {
                        setEditingCommentId(comment.id)
                        setEditingCommentBody(comment.body)
                      }}
                      onCancelEdit={() => {
                        setEditingCommentId(null)
                        setEditingCommentBody('')
                      }}
                      onSaveEdit={handleSaveComment}
                      onDelete={handleDeleteComment}
                    />
                  ) : (
                    <p className="text-sm text-ink-500">
                      You do not have permission to view review feedback.
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'comments' && (
                <CaseCommentsPanel
                  caseItem={caseItem}
                  comments={nationalComments}
                  channel="national"
                  isConsultant={session?.user.id === caseItem.belongs_to_doctor_id}
                  canStore={
                    can('case-comments-store') &&
                    Boolean(caseItem.is_global) &&
                    !isReviewerOnly
                  }
                  canUpdate={can('case-comments-update')}
                  canDelete={can('case-comments-delete')}
                  isUnderReview={isUnderReview}
                  nationalCommentsEnabled={Boolean(
                    caseItem.is_global && caseItem.allow_national_comments,
                  )}
                  actionLoading={actionLoading}
                  commentBody={commentBody}
                  onCommentBodyChange={(value) => {
                    setCommentChannel('national')
                    setCommentBody(value)
                  }}
                  postAnonymously={postAnonymously}
                  onPostAnonymouslyChange={setPostAnonymously}
                  onAddComment={() => handleAddComment('national')}
                  editingCommentId={editingCommentId}
                  editingCommentBody={editingCommentBody}
                  onEditingCommentBodyChange={setEditingCommentBody}
                  onStartEdit={(comment) => {
                    setEditingCommentId(comment.id)
                    setEditingCommentBody(comment.body)
                  }}
                  onCancelEdit={() => {
                    setEditingCommentId(null)
                    setEditingCommentBody('')
                  }}
                  onSaveEdit={handleSaveComment}
                  onDelete={handleDeleteComment}
                />
              )}

              {activeTab === 'files' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="font-display text-lg font-semibold">
                      Files / media
                    </h2>
                    <div className="mt-4">
                      <CaseFilesGallery
                        files={files}
                        actionLoading={actionLoading}
                        canDownload={can('case-files-download')}
                        canDelete={can('case-files-delete') && canMutateCurrentCase}
                        onDownload={(fileId) => void handleDownloadFile(fileId)}
                        onDelete={handleDeleteFile}
                      />
                    </div>
                  </div>

                  {canMutateCurrentCase && (
                    <PermissionGate permission="case-files-upload">
                    <CaseMediaUploader
                      files={pendingMedia}
                      onChange={setPendingMedia}
                      disabled={
                        actionLoading || Boolean(caseItem.deleted_at) || isArchived
                      }
                      uploading={actionLoading}
                      onUpload={handleUploadFiles}
                      title="Upload multiple files"
                    />
                    </PermissionGate>
                  )}
                </div>
              )}

              {activeTab === 'activity' && (
                <div>
                  <h2 className="font-display text-lg font-semibold">
                    Activity timeline
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {activity.length === 0 ? (
                      <li className="text-sm text-ink-500">No activity recorded.</li>
                    ) : (
                      activity.map((entry) => (
                        <li
                          key={entry.id}
                          className="rounded-md border border-parch-100 bg-parch-50 px-3 py-3 text-sm"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-medium text-ink-800">
                              {entry.description}
                            </p>
                            <p className="text-xs text-ink-500">
                              {entry.created_at
                                ? new Date(entry.created_at).toLocaleString()
                                : ''}
                            </p>
                          </div>
                          <p className="mt-1 text-xs text-ink-500">
                            {entry.actor?.name ?? entry.actor_id}
                            {entry.type ? ` · ${entry.type}` : ''}
                          </p>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </section>
          </>
        ) : null}

        <SoftDeleteConfirmDialog
          open={deleteOpen}
          entityLabel="case"
          itemName={caseItem?.reference ?? ''}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={() => void handleDeleteConfirm()}
          confirming={actionLoading}
        />
      </div>
    </AppShell>
  )
}
